"use client";

import * as React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { icons } from "../../assets/icons";
import {
  validarRolYGetApartamentos,
  confirmarAccesoFinal,
  type RolUsuario,
} from "../../api/authService";
import { useAuth } from "../../context/AuthContext";


const CODIGOS_PERMITIDOS = ["AD", "AS", "MA", "PT", "SO"];

export default function RoleSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginFinalize } = useAuth();

  const { email, password, usuario, unidadId, unidadNombre, roles, unidades } = location.state || {
    email: "",
    password: "",
    usuario: null,
    unidadId: "",
    unidadNombre: "Unidad seleccionada",
    roles: [] as RolUsuario[],
    unidades: [],
  };

  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const onSelect = async (rolId: string, rolNombre: string) => {
    try {
      setError(null);
      setLoadingId(rolId);

      if (!CODIGOS_PERMITIDOS.includes(rolId)) {
        setError("No tienes permisos para acceder con este rol.");
        return;
      }

      const { apartamentos } = await validarRolYGetApartamentos({
        email,
        contrasena: password,
        CodigoUnidadResidencial: unidadId,
        TipoUsuario: rolId,
      });

      if (apartamentos.length > 1) {
        navigate("/ApartmentSelection", {
          state: {
            email,
            password,
            usuario,
            unidadId,
            unidadNombre,
            rolId,
            rolNombre,
            apartamentos,
            roles, 
            unidades, 
          },
        });
        return;
      }

      if (apartamentos.length === 1) {
        const apt = apartamentos[0];

        await confirmarAccesoFinal({
          email,
          contrasena: password,
          CodigoUnidadResidencial: unidadId,
          TipoUsuario: rolId,
          CodigoApartamento: apt.id,
        });

        await loginFinalize({
          email,
          usuario,
          unidad: { id: unidadId, nombre: unidadNombre },
          rol: { id: rolId, nombre: rolNombre },
          apartamento: { id: apt.id, nombre: apt.nombre },
        });

        navigate("/dashboard", {
          state: {
            unit: unidadNombre,
            role: rolNombre,
            apartment: apt.nombre,
          },
        });
        return;
      }

      try {
        await confirmarAccesoFinal({
          email,
          contrasena: password,
          CodigoUnidadResidencial: unidadId,
          TipoUsuario: rolId,
          CodigoApartamento: "",
        });

        await loginFinalize({
          email,
          usuario,
          unidad: { id: unidadId, nombre: unidadNombre },
          rol: { id: rolId, nombre: rolNombre },
        });

        navigate("/dashboard", {
          state: {
            unit: unidadNombre,
            role: rolNombre,
          },
        });
      } catch {
        setError(
          "No se encontraron apartamentos para este rol. Contacta a la administración."
        );
      }
    } catch (e: any) {
      setError(e?.message || "No fue posible validar el rol seleccionado.");
    } finally {
      setLoadingId(null);
    }
  };

  React.useEffect(() => {
    if (!email || !password || !roles.length) {
      navigate("/");
    }
  }, [email, password, roles, navigate]);

  const handleGoBack = () => {
    navigate("/UnitSelectionPage", { 
      replace: true, 
      state: { 
        email, 
        password, 
        usuario, 
        unidades
      } 
    });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 px-4">
      <Card
        className="w-full max-w-lg shadow-xl border border-gray-200 rounded-2xl"
        style={{ backgroundColor: "rgb(247,247,247)" }}
      >
        <CardHeader>
          <div className="flex flex-col items-center text-center">
            <img src={icons.logo} alt="EasyPH Logo" className="w-60 h-auto" />
            <Button
              onClick={handleGoBack}
              className="mt-2 w-full text-white transition-all duration-200 shadow-md font-medium py-2 px-4 rounded-lg"
              style={{
                backgroundColor: "#DF8037",
                borderColor: "#DF8037",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#E55A00";
                e.currentTarget.style.borderColor = "#E55A00";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#DF8037";
                e.currentTarget.style.borderColor = "#DF8037";
              }}
            >
              Regresar a Unidades
            </Button>
            <CardTitle className="text-2xl font-bold text-military-600 mt-3">
              {email}
            </CardTitle>
            <p className="text-base font-medium text-military-600 mt-3">
              {unidadNombre}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            {roles.map((role: RolUsuario) => {
              const isPermitted = CODIGOS_PERMITIDOS.includes(role.id);

              return (
                <Button
                  key={role.id}
                  onClick={() => onSelect(role.id, role.nombre)}
                  disabled={loadingId === role.id || !isPermitted}
                  className={`w-full shadow-md rounded-lg disabled:opacity-50 ${
                    isPermitted
                      ? "bg-military-500 hover:bg-military-600 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {loadingId === role.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Validando...
                    </>
                  ) : (
                    <>
                      {role.nombre}
                      {!isPermitted && (
                        <span className="ml-2 text-xs">(Sin permisos)</span>
                      )}
                    </>
                  )}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}